// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation } from '@apollo/client';
import { Icon, useTranslate, Text } from '@plesk/ui-library';

import HIDE_PROMO from './HideStickerCollectionPromo.graphql';

// Easter egg "My 80s Collection" (Plesk 18.0.80). Safe to drop with the egg.

const elvis = () => require('images/sticker-collection/sticker/elvis.png');
const stars = () => require('images/sticker-collection/badge-stars.png');
const ghost = () => require('images/sticker-collection/sticker/ghost.png');

const FAREWELL_DURATION = 6000;

const PAGE_KEYS: Record<string, string> = {
    ADMIN: 'Promo.adminPage',
    RESELLER: 'Promo.resellerPage',
};

interface PromoProps {
    count: number;
    userType: string;
    promoHidden: boolean;
}

const Promo = ({ count, userType, promoHidden }: PromoProps) => {
    const navigate = useNavigate();
    const translate = useTranslate('components.layouts.StickerCollectionGame');
    const [hidePromo] = useMutation(HIDE_PROMO);
    const [closing, setClosing] = useState(false);
    const [gone, setGone] = useState(false);

    useEffect(() => {
        if (!closing) {
            return undefined;
        }
        const timer = setTimeout(() => setGone(true), FAREWELL_DURATION);
        return () => clearTimeout(timer);
    }, [closing]);

    const close = () => {
        setClosing(true);
        hidePromo();
    };

    if (gone) {
        return null;
    }

    if (closing) {
        return (
            <div className="sticker-collection-farewell" aria-hidden="true">
                <div className="sticker-collection-farewell__box">
                    <img className="sticker-collection-farewell__ghost" src={ghost()} alt="" />
                    <p className="sticker-collection-farewell__text">
                        {translate('Promo.farewell', {
                            page: <Text bold>{translate(PAGE_KEYS[userType] ?? 'Promo.accountPage')}</Text>,
                        })}
                    </p>
                </div>
            </div>
        );
    }

    if (promoHidden) {
        return null;
    }

    return (
        <div className="sticker-collection-promo">
            <button
                data-action="sticker-collection-promo"
                type="button"
                className="sticker-collection-promo__link"
                onClick={() => navigate('/cp/sticker-collection')}
            >
                <span className="sticker-collection-promo__avatar">
                    <span className="sticker-collection-promo__frame">
                        <img className="sticker-collection-promo__elvis" src={elvis()} alt="" />
                    </span>
                    <img className="sticker-collection-promo__stars" src={stars()} alt="" />
                    <span className="sticker-collection-promo__counter">{count}</span>
                </span>
                <span className="sticker-collection-promo__title">{translate('Promo.title')}</span>
            </button>
            <button
                data-action="close-sticker-collection-promo"
                type="button"
                className="sticker-collection-promo__close"
                aria-label={translate('Promo.close') as string}
                onClick={close}
            >
                <Icon name="cross-mark" size="12" />
            </button>
        </div>
    );
};

export default Promo;
